import { NextPage } from 'next';
import React, { FC } from 'react';
import JsonLd from '../../../../components/layouts/JsonLd';
import MetaTags from '../../../../components/layouts/MetaTags';
import MerchantBlogPostPage from '../../../../templates/merchants/blogs/DetailTemplate/MerchantBlogPostPage';
import {
  allCategoriesCache,
  getCategory,
} from '../../../../infrastructure/api/backend/explore_api';
import { CategoryEntity } from '../../../../interfaces/explore/CategoryEntity';
import { MerchantDetail } from '../../../../interfaces/merchants';
import { MerchantBlogEntry } from '../../../../interfaces/merchants/blogs';
import { fetchMerchantDetail } from '../../../../infrastructure/api';
import { newSecuredAxios } from '../../../../utils/secured-axios';
import {
  AppGetServerSidePropsContext,
  injectAppContextGetServerSideProps,
} from '../../../_app';
import ErrorTemplate from '../../../../templates/ErrorTemplate';
import { MerchantSite } from '../../../../components/MerchantSite';
import { usePushMerchantDataLayerEffect } from '../../../../hooks/gtm';
import { buildApiV2Url, buildSiteUrl } from 'src/utils/request-url';

interface Context extends AppGetServerSidePropsContext {
  query: {
    publicId: string;
    blogId: string;
  };
}

interface Props {
  post?: MerchantBlogEntry;
  merchantDetail?: MerchantDetail;
  categories: CategoryEntity[];
  category?: CategoryEntity;
  isUnpublishedSite: boolean;
  isCoubicApp: boolean;
}

const BlogComponent: FC<Props> = ({
  merchantDetail,
  category,
  post,
  isCoubicApp,
}) => {
  usePushMerchantDataLayerEffect({ visit_merchant_id: merchantDetail?.id });

  if (merchantDetail && category && post) {
    const metaThumbnailImageUrl = post.primary_image
      ? post.primary_image.p1280x1280.url
      : merchantDetail.cover_image.url;
    return (
      <>
        <MetaTags
          title={post.name}
          description={
            post.description_text ? post.description_text.slice(0, 100) : ''
          }
          keywords={merchantDetail.seo_setting?.keyword || ''}
          thumbnailUrl={metaThumbnailImageUrl}
          currentPath={buildSiteUrl(`/blogs/${post.public_id}`)}
          noIndex={merchantDetail.seo_setting?.no_index}
          googleAnalyticsFourTrackingId={
            merchantDetail.available_google_analytics_four_tracking_id
          }
          faviconImageUrl={merchantDetail?.favicon_image?.url}
        />
        <JsonLd merchant={merchantDetail} />
        <MerchantBlogPostPage
          post={post}
          merchantDetail={merchantDetail}
          category={category}
          isCoubicApp={isCoubicApp}
        />
      </>
    );
  }
  return <ErrorTemplate statusCode={404} />;
};

const MerchantBlogShow: NextPage<Props> = (props: Props) => {
  const { merchantDetail, isUnpublishedSite } = props;
  return (
    <MerchantSite
      isUnPublished={isUnpublishedSite}
      isMerchantInfoExist={!!merchantDetail}
      logoImage={merchantDetail?.logo_image || null}
      merchantPublicId={merchantDetail?.public_id || null}
      displayName={merchantDetail?.display_name || null}
      isCoubicApp={props.isCoubicApp}
    >
      <BlogComponent {...props} />
    </MerchantSite>
  );
};

export const getServerSideProps = injectAppContextGetServerSideProps<Props>(
  async ({ query, session, token, requestId, isCoubicApp }: Context) => {
    const pBlog = newSecuredAxios({
      session,
      token,
      requestId,
    }).get<MerchantBlogEntry>(
      buildApiV2Url(`/merchants/${query.publicId}/posts/${query.blogId}`),
    );

    const { publicId: merchantPublicId } = query;

    const merchantDetailResponse = await fetchMerchantDetail({
      session,
      token,
      requestId,
      merchantPublicId,
    });
    if (merchantDetailResponse.redirect) {
      return { redirect: merchantDetailResponse.redirect };
    }
    const { merchant, isUnpublishedSite } = merchantDetailResponse;

    if (isUnpublishedSite) {
      return {
        props: {
          merchantDetail: merchant,
          categories: [],
          isUnpublishedSite,
          isCoubicApp,
        },
      };
    }

    const [categories, blog] = await Promise.all([
      // Layoutで取得している部分を徐々に置き換え
      allCategoriesCache(),
      pBlog,
    ]);

    const category = merchant
      ? await getCategory(merchant.category_id)
      : undefined;

    const props: Props = {
      merchantDetail: merchant,
      categories,
      category,
      post: blog.data,
      isUnpublishedSite,
      isCoubicApp,
    };

    return { props };
  },
);

export default MerchantBlogShow;
